import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Code, Book, Key, Shield, Database, Wifi, Map, Plane, Ship, Search, ArrowLeft, Copy, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SEOHead } from '@/components/SEOHead'

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'https://api.sycek.io'

export default function APIDocumentation() {
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedCode(id)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  const codeExamples = {
    curl: `curl -X POST ${API_BASE_URL}/breach/search \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "query": "user@example.com"
  }'`,
    python: `import requests

url = "${API_BASE_URL}/breach/search"
headers = {
    "Authorization": "Bearer YOUR_API_TOKEN",
    "Content-Type": "application/json"
}
data = {
    "query": "user@example.com"
}

response = requests.post(url, json=data, headers=headers)
result = response.json()
print(result)`,
    javascript: `const response = await fetch('${API_BASE_URL}/breach/search', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_TOKEN',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    query: 'user@example.com'
  })
})

const result = await response.json()
console.log(result)`,
  }

  const endpointSections = [
    {
      id: 'authentication',
      title: 'Authentication',
      icon: Key,
      color: 'text-yellow-400',
      endpoints: [
        {
          method: 'POST',
          path: '/auth/register-new',
          description: 'Register a new user account',
          auth: false,
          request: {
            email: 'string',
            password: 'string',
            first_name: 'string',
            last_name: 'string'
          },
          response: {
            id: 'string',
            email: 'string',
            first_name: 'string',
            last_name: 'string',
            role: 'string',
            subscription_tier: 'string'
          }
        },
        {
          method: 'POST',
          path: '/auth/login-new',
          description: 'Authenticate and receive access token',
          auth: false,
          request: {
            email: 'string',
            password: 'string'
          },
          response: {
            access_token: 'string',
            token_type: 'bearer',
            user: 'object',
            expires_in: 1800
          }
        },
        {
          method: 'POST',
          path: '/auth/forgot-password',
          description: 'Request password reset email',
          auth: false,
          request: {
            email: 'string'
          },
          response: {
            message: 'string'
          }
        },
        {
          method: 'POST',
          path: '/auth/reset-password',
          description: 'Reset password using token',
          auth: false,
          request: {
            token: 'string',
            new_password: 'string'
          },
          response: {
            message: 'string'
          }
        },
        {
          method: 'GET',
          path: '/users/me',
          description: 'Get current user information',
          auth: true,
          response: {
            id: 'string',
            email: 'string',
            subscription_tier: 'string',
            request_limit: 'number',
            requests_used: 'number'
          }
        }
      ]
    },
    {
      id: 'breachint',
      title: 'BreachINT',
      icon: Shield,
      color: 'text-red-400',
      endpoints: [
        {
          method: 'POST',
          path: '/breach/search',
          description: 'Search for breach intelligence data including email, phone, username, or domain',
          auth: true,
          request: {
            query: 'string'
          },
          response: {
            nodes: 'array',
            edges: 'array',
            summary: 'object'
          },
          example: {
            query: 'user@example.com'
          }
        }
      ]
    },
    {
      id: 'socint',
      title: 'SOCINT',
      icon: Search,
      color: 'text-blue-400',
      endpoints: [
        {
          method: 'POST',
          path: '/socint/search',
          description: 'Social media intelligence search across multiple platforms',
          auth: true,
          request: {
            query: 'string',
            platforms: 'array'
          },
          response: {
            nodes: 'array',
            edges: 'array'
          }
        },
        {
          method: 'POST',
          path: '/socint/enhanced-search',
          description: 'Enhanced SOCINT search with AI analysis',
          auth: true,
          request: {
            query: 'string'
          },
          response: {
            nodes: 'array',
            edges: 'array'
          }
        },
        {
          method: 'POST',
          path: '/socint/layered-search',
          description: 'Multi-layer deep search across social platforms',
          auth: true,
          request: {
            query: 'string'
          },
          response: {
            layers: 'array'
          }
        }
      ]
    },
    {
      id: 'geoint',
      title: 'GEOINT',
      icon: Map,
      color: 'text-green-400',
      endpoints: [
        {
          method: 'POST',
          path: '/geoint/aoi',
          description: 'Create an Area of Interest (AOI)',
          auth: true,
          request: {
            name: 'string',
            coordinates: 'array',
            description: 'string'
          },
          response: {
            id: 'string',
            name: 'string',
            status: 'string'
          }
        },
        {
          method: 'GET',
          path: '/geoint/imagery/{aoi_id}',
          description: 'Get satellite imagery for an AOI',
          auth: true,
          response: {
            imagery: 'array',
            timestamps: 'array'
          }
        }
      ]
    },
    {
      id: 'air-traffic',
      title: 'Air Traffic Intelligence',
      icon: Plane,
      color: 'text-purple-400',
      endpoints: [
        {
          method: 'POST',
          path: '/air-traffic/flights',
          description: 'Search for aircraft and flight information',
          auth: true,
          request: {
            query: 'string',
            region: 'string'
          },
          response: {
            flights: 'array',
            total: 'number'
          }
        },
        {
          method: 'POST',
          path: '/air-traffic/summary',
          description: 'Get air traffic summary for a region',
          auth: true,
          request: {
            region: 'string',
            time_range: 'object'
          },
          response: {
            total_flights: 'number',
            active_aircraft: 'number'
          }
        }
      ]
    },
    {
      id: 'maritime',
      title: 'Maritime Intelligence',
      icon: Ship,
      color: 'text-cyan-400',
      endpoints: [
        {
          method: 'POST',
          path: '/maritime/search',
          description: 'Search for vessel information and maritime data',
          auth: true,
          request: {
            query: 'string',
            vessel_type: 'string'
          },
          response: {
            vessels: 'array',
            total: 'number'
          }
        }
      ]
    },
    {
      id: 'wifi',
      title: 'WiFi Intelligence',
      icon: Wifi,
      color: 'text-orange-400',
      endpoints: [
        {
          method: 'POST',
          path: '/wifi/networks',
          description: 'Search WiFi networks using WiGLE database',
          auth: true,
          request: {
            ssid: 'string',
            latitude: 'number',
            longitude: 'number',
            radius_km: 'number'
          },
          response: {
            networks: 'array',
            totalResults: 'number'
          }
        }
      ]
    },
    {
      id: 'cases',
      title: 'Case Management',
      icon: Database,
      color: 'text-pink-400',
      endpoints: [
        {
          method: 'GET',
          path: '/cases',
          description: 'Get all investigation cases',
          auth: true,
          response: {
            cases: 'array',
            total: 'number'
          }
        },
        {
          method: 'POST',
          path: '/cases',
          description: 'Create a new investigation case',
          auth: true,
          request: {
            title: 'string',
            description: 'string',
            tags: 'array'
          },
          response: {
            id: 'string',
            title: 'string',
            status: 'string'
          }
        }
      ]
    }
  ]

  return (
    <>
      <SEOHead
        title="API Documentation - Sycek OSINT | Developer Guide & Integration"
        description="Comprehensive API documentation for Sycek OSINT platform. Learn how to integrate breach intelligence, GEOINT, SOCINT, and threat detection APIs into your applications. Includes code examples in cURL, Python, and JavaScript."
        keywords="OSINT API, cyber intelligence API, breach intelligence API, GEOINT API, SOCINT API, threat detection API, cybersecurity API integration, API documentation, REST API, JSON API, developer guide, API examples, cyber intelligence SDK, security API, digital forensics API, threat intelligence API, API authentication, rate limiting, API endpoints, webhook integration, API testing, security analytics API, intelligence platform API"
        canonical="/api-docs"
      />
      <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 via-purple-900/20 to-red-900/20"></div>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f61a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f61a_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

      {/* Header */}
      <div className="relative z-10 border-b border-cyan-500/30 bg-black/90 backdrop-blur-sm shadow-[0_4px_20px_rgba(34,211,238,0.15)]">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img 
              src="/logo.jpeg" 
              alt="Sycek Logo" 
              className="h-10 w-10 object-contain drop-shadow-[0_0_15px_rgba(34,197,94,0.6)]"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent drop-shadow-[0_0_10px_rgba(34,211,238,0.6)] font-mono">
                SYCEK OSINT
              </h1>
              <p className="text-xs text-green-400 font-mono drop-shadow-[0_0_5px_rgba(34,197,94,0.6)]">[SYSTEM ACTIVE]</p>
            </div>
          </Link>
          <div className="flex items-center space-x-4">
            <Link to="/">
              <Button variant="ghost" className="text-cyan-400 hover:bg-cyan-500/20 border border-cyan-500/50">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <a href={`${API_BASE_URL}/docs`} target="_blank" rel="noopener noreferrer">
              <Button className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500">
                <Code className="w-4 h-4 mr-2" />
                Interactive API Docs
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-12 max-w-6xl">
        {/* Hero Section */}
        <div className="mb-12 text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Book className="w-10 h-10 text-cyan-400" />
            <h1 className="text-5xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-green-400 bg-clip-text text-transparent">
              API Documentation
            </h1>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto font-light">
            Comprehensive guide to integrating with the Sycek OSINT API. Build powerful intelligence applications.
          </p>
          <div className="mt-6 flex items-center justify-center gap-4 text-sm text-gray-400">
            <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
              API v0.1.0
            </Badge>
            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">
              REST API
            </Badge>
            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50">
              JSON Responses
            </Badge>
          </div>
        </div>

        {/* Getting Started */}
        <Card className="bg-gray-900/50 border border-cyan-500/30 backdrop-blur-sm mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-cyan-400">
              <Code className="w-6 h-6 mr-2" />
              Getting Started
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-green-400 mb-2">Base URL</h3>
              <div className="bg-gray-800/50 border border-cyan-500/30 rounded-lg p-4 font-mono text-sm">
                <div className="flex items-center justify-between">
                  <code className="text-cyan-400">{API_BASE_URL}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(API_BASE_URL, 'base-url')}
                    className="text-gray-400 hover:text-cyan-400"
                  >
                    {copiedCode === 'base-url' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-green-400 mb-2">Authentication</h3>
              <p className="text-gray-300 mb-3">
                Most endpoints require authentication using a Bearer token. Obtain your token by logging in through the API.
              </p>
              <div className="bg-gray-800/50 border border-yellow-500/30 rounded-lg p-4 font-mono text-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-yellow-400">Authorization Header:</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard('Authorization: Bearer YOUR_API_TOKEN', 'auth-header')}
                    className="text-gray-400 hover:text-yellow-400"
                  >
                    {copiedCode === 'auth-header' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <code className="text-gray-300">Authorization: Bearer YOUR_API_TOKEN</code>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-green-400 mb-2">Rate Limiting</h3>
              <p className="text-gray-300 mb-2">
                API requests are rate-limited based on your subscription tier:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-300 ml-4">
                <li><strong className="text-cyan-400">Starter:</strong> 5 queries per month</li>
                <li><strong className="text-purple-400">Pro:</strong> 100 queries per month</li>
                <li><strong className="text-green-400">Advance:</strong> 250 queries per month</li>
                <li><strong className="text-yellow-400">Enterprise:</strong> Custom/unlimited</li>
              </ul>
              <p className="text-yellow-400 text-sm mt-2 font-mono">
                Rate limit headers: X-RateLimit-Limit, X-RateLimit-Remaining, X-RateLimit-Reset
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-green-400 mb-2">Example Request</h3>
              <Tabs defaultValue="curl" className="w-full">
                <TabsList className="grid w-full grid-cols-3 bg-gray-800/50">
                  <TabsTrigger value="curl">cURL</TabsTrigger>
                  <TabsTrigger value="python">Python</TabsTrigger>
                  <TabsTrigger value="javascript">JavaScript</TabsTrigger>
                </TabsList>
                {Object.entries(codeExamples).map(([lang, code]) => (
                  <TabsContent key={lang} value={lang} className="mt-4">
                    <div className="bg-gray-800/50 border border-cyan-500/30 rounded-lg p-4 font-mono text-sm relative">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(code, `example-${lang}`)}
                        className="absolute top-2 right-2 text-gray-400 hover:text-cyan-400"
                      >
                        {copiedCode === `example-${lang}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      <pre className="text-gray-300 overflow-x-auto">
                        <code>{code}</code>
                      </pre>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </CardContent>
        </Card>

        {/* Endpoints by Category */}
        <div className="space-y-8">
          {endpointSections.map((section) => (
            <Card key={section.id} className="bg-gray-900/50 border border-cyan-500/30 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <section.icon className={`w-6 h-6 mr-3 ${section.color}`} />
                  <span>{section.title}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {section.endpoints.map((endpoint, idx) => (
                  <div key={idx} className="border-b border-cyan-500/20 pb-6 last:border-0 last:pb-0">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge
                            className={
                              endpoint.method === 'GET'
                                ? 'bg-green-500/20 text-green-400 border-green-500/50'
                                : endpoint.method === 'POST'
                                ? 'bg-blue-500/20 text-blue-400 border-blue-500/50'
                                : endpoint.method === 'PUT'
                                ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
                                : 'bg-red-500/20 text-red-400 border-red-500/50'
                            }
                          >
                            {endpoint.method}
                          </Badge>
                          <code className="text-cyan-400 font-mono text-lg">{endpoint.path}</code>
                          {endpoint.auth && (
                            <Badge variant="outline" className="text-yellow-400 border-yellow-500/50">
                              Requires Auth
                            </Badge>
                          )}
                        </div>
                        <p className="text-gray-300">{endpoint.description}</p>
                      </div>
                    </div>

                    {endpoint.request && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-purple-400 mb-2">Request Body</h4>
                        <div className="bg-gray-800/50 border border-purple-500/30 rounded-lg p-4 font-mono text-sm">
                          <pre className="text-gray-300 overflow-x-auto">
                            <code>{JSON.stringify(endpoint.request, null, 2)}</code>
                          </pre>
                        </div>
                      </div>
                    )}

                    {'example' in endpoint && endpoint.example && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-cyan-400 mb-2">Example Request</h4>
                        <div className="bg-gray-800/50 border border-cyan-500/30 rounded-lg p-4 font-mono text-sm">
                          <pre className="text-gray-300 overflow-x-auto">
                            <code>{JSON.stringify(endpoint.example, null, 2)}</code>
                          </pre>
                        </div>
                      </div>
                    )}

                    {endpoint.response && (
                      <div>
                        <h4 className="text-sm font-semibold text-green-400 mb-2">Response</h4>
                        <div className="bg-gray-800/50 border border-green-500/30 rounded-lg p-4 font-mono text-sm">
                          <pre className="text-gray-300 overflow-x-auto">
                            <code>{JSON.stringify(endpoint.response, null, 2)}</code>
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Error Handling */}
        <Card className="bg-gray-900/50 border border-red-500/30 backdrop-blur-sm mt-8">
          <CardHeader>
            <CardTitle className="flex items-center text-red-400">
              <Shield className="w-6 h-6 mr-2" />
              Error Handling
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-300">
              The API uses standard HTTP status codes. Error responses include a message field with details:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-800/50 border border-red-500/30 rounded-lg p-4">
                <Badge className="bg-red-500/20 text-red-400 border-red-500/50 mb-2">400 Bad Request</Badge>
                <p className="text-sm text-gray-300">Invalid request parameters or malformed data</p>
              </div>
              <div className="bg-gray-800/50 border border-red-500/30 rounded-lg p-4">
                <Badge className="bg-red-500/20 text-red-400 border-red-500/50 mb-2">401 Unauthorized</Badge>
                <p className="text-sm text-gray-300">Missing or invalid authentication token</p>
              </div>
              <div className="bg-gray-800/50 border border-red-500/30 rounded-lg p-4">
                <Badge className="bg-red-500/20 text-red-400 border-red-500/50 mb-2">403 Forbidden</Badge>
                <p className="text-sm text-gray-300">Insufficient permissions for this resource</p>
              </div>
              <div className="bg-gray-800/50 border border-red-500/30 rounded-lg p-4">
                <Badge className="bg-red-500/20 text-red-400 border-red-500/50 mb-2">429 Too Many Requests</Badge>
                <p className="text-sm text-gray-300">Rate limit exceeded. Check X-RateLimit headers</p>
              </div>
              <div className="bg-gray-800/50 border border-red-500/30 rounded-lg p-4">
                <Badge className="bg-red-500/20 text-red-400 border-red-500/50 mb-2">500 Internal Server Error</Badge>
                <p className="text-sm text-gray-300">Server error. Contact support if persistent</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Support Section */}
        <Card className="bg-gray-900/50 border border-cyan-500/30 backdrop-blur-sm mt-8">
          <CardHeader>
            <CardTitle className="text-cyan-400">Need Help?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-green-400 mb-2">Support</h4>
                <p className="text-sm text-gray-300 mb-2">Email: support@sycek.io</p>
                <p className="text-sm text-gray-300">Response time: Within 24 hours</p>
              </div>
              <div>
                <h4 className="font-semibold text-blue-400 mb-2">Interactive Docs</h4>
                <p className="text-sm text-gray-300 mb-2">
                  Try our interactive Swagger documentation:
                </p>
                <a href={`${API_BASE_URL}/docs`} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline font-mono text-sm">
                  {API_BASE_URL}/docs
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  )
}

